/**
 * Utility functions for generating placeholder images
 */

/**
 * สร้าง SVG placeholder image แบบ inline base64
 * @param {number} width - ความกว้างของรูปภาพ
 * @param {number} height - ความสูงของรูปภาพ  
 * @param {string} text - ข้อความที่จะแสดง
 * @param {string} bgColor - สีพื้นหลัง (hex format เช่น #cccccc)
 * @param {string} textColor - สีข้อความ (hex format เช่น #ffffff)
 * @returns {string} Data URL ของ SVG image
 */
export const createPlaceholderImage = (width = 400, height = 300, text = 'ไม่มีรูปภาพ', bgColor = '#4a5568', textColor = '#9fa8e1') => {
  const svg = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="${bgColor}"/>
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="20" fill="${textColor}" text-anchor="middle" dy=".3em">${text}</text>
    </svg>
  `;
  
  // แปลงเป็น base64
  const base64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${base64}`;
};

/**
 * Placeholder images ที่ใช้บ่อย
 */
export const placeholderImages = {
  // รูปภาพอัลบั้มเปล่า
  noImages: createPlaceholderImage(400, 300, 'กำลังโหลดรูปภาพ...', '#4a5568', '#9fa8e1'),
  
  // รูปภาพไม่พบ
  imageNotFound: createPlaceholderImage(400, 300, 'ไม่สามารถโหลดได้', '#ef4444', '#ffffff'),
  
  // รูปโปรไฟล์เปล่า
  noAvatar: createPlaceholderImage(100, 100, '👤', '#6b7280', '#ffffff'),
  
  // รูปเกมเปล่า
  noGameImage: createPlaceholderImage(200, 200, '🎮', '#3b82f6', '#ffffff'),
  
  // รูปบริการเปล่า
  noServiceImage: createPlaceholderImage(300, 200, '⚙️', '#8b5cf6', '#ffffff'),
  
  // รูปข่าวเปล่า
  noNewsImage: createPlaceholderImage(400, 250, '📰', '#10b981', '#ffffff'),
};

/**
 * Hook สำหรับจัดการ placeholder images
 * @param {string} type - ประเภทของ placeholder
 * @returns {object} Object ที่มี placeholder URL และ error handler
 */
export const usePlaceholder = (type = 'noImages') => {
  const placeholderUrl = placeholderImages[type] || placeholderImages.noImages;
  
  const handleImageError = (e) => {
    // ถ้ารูปโหลดไม่ได้ให้ใช้ placeholder
    if (e.target.src !== placeholderUrl) {
      e.target.src = placeholderUrl;
    }
  };
  
  return {
    placeholderUrl,
    onError: handleImageError
  };
};

/**
 * Component สำหรับแสดง placeholder image
 */
export const PlaceholderImage = ({ 
  width = 400, 
  height = 300, 
  text = 'ไม่มีรูปภาพ', 
  bgColor = '#4a5568', 
  textColor = '#9fa8e1',
  className = '',
  alt = 'Placeholder'
}) => {
  const placeholderUrl = createPlaceholderImage(width, height, text, bgColor, textColor);
  
  return (
    <img 
      src={placeholderUrl}
      alt={alt}
      className={className}
      style={{ width, height }}
    />
  );
};

export default {
  createPlaceholderImage,
  placeholderImages,
  usePlaceholder,
  PlaceholderImage
};
